import { action, observable } from 'mobx';
import { Api } from '../../api';
import { SurveyStatus, Survey } from '../../survey';
import { VisibilityChangeSubscription } from './VisibilityChangeSubscription';
import { PrefersColorSchemeSubscription } from './PrefersColorSchemeSubscription';
import { CmIdNotFoundError } from './error-types';
import { type Theme, type ThemeId, THEMES } from '../themes/themes';

/**
 * Управление состоянием приложения (инициализация, создание опроса, работа с ошибками, ...)
 */
export class App {
  @observable.ref accessor survey: Survey | null = null;
  @observable accessor initialized: boolean = false;
  @observable accessor initializing: boolean = false;
  @observable.ref accessor error: unknown = null;
  @observable.ref accessor currentTheme: Theme = THEMES.DEFAULT;
  @observable accessor isDarkMode: boolean = false;

  constructor() {
    new PrefersColorSchemeSubscription(this);
    new VisibilityChangeSubscription(this);
  }

  @action
  private set_initialized(initialized: boolean): void {
    this.initialized = initialized;
  }

  @action
  private set_initializing(initializing: boolean): void {
    this.initializing = initializing;
  }

  @action
  set_error(err: unknown): void {
    this.error = err;
  }

  @action
  private set_survey(survey: Survey | null): void {
    this.survey = survey;
  }

  @action
  set_isDarkMode(value: boolean): void {
    if (this.isDarkMode !== value) {
      this.isDarkMode = value;
      this.applyTheme(this.currentTheme.id);
    }
  }

  @action
  async init(cmId?: string | null): Promise<void> {
    try {
      if (this.initializing) {
        return;
      }

      this.set_initializing(true);
      this.set_error(null);

      if (!cmId) {
        throw new CmIdNotFoundError();
      }

      await Api.createSession();

      const { formVersionId, questions, maxProgressStepsCount, defaultLastPageButtonProps, design } = await Api.fetchSurveyForm(cmId);

      this.applyTheme(design);

      await Api.sendSurveyStatus(
        {
          id: formVersionId,
          cm_id: cmId,
          status: SurveyStatus.ENTERED
        },
        false
      );

      this.set_survey(new Survey(this, cmId, formVersionId, questions, defaultLastPageButtonProps, maxProgressStepsCount));
    } catch (err: unknown) {
      this.set_error(err);
    } finally {
      this.set_initialized(true);
      this.set_initializing(false);
    }
  }

  @action
  private applyTheme(designId: ThemeId): void {
    const theme = Object.values(THEMES).find((t) => t.id === designId) || THEMES.DEFAULT;
    this.currentTheme = theme;

    // Применяем CSS-переменные темы
    Object.entries(theme.cssVariables).forEach(([key, value]: [string, string]) => {
      document.documentElement.style.setProperty(key, value);
    });

    // Если тёмный режим активен и есть переопределения для dark, применяем их
    if (this.isDarkMode && theme.overrides?.dark) {
      Object.entries(theme.overrides.dark).forEach(([key, value]: [string, string]) => {
        document.documentElement.style.setProperty(key, value);
      });
    }
  }
}
